import {useCallback, useEffect, useMemo, useState} from 'react'

import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import {isValidPhoneNumber} from 'libphonenumber-js'

import {P} from '@festi/common/components/typography'
import {NewInput} from '@festi/common/components/form'
import {Modal} from '@festi/common/components/common'
import {parsePhoneNumberNoPrefix} from '@festi/utils/strings'
import {useAuth} from '@festi/common/contexts'

import {KycAnswer, usePago} from '../PagoContext'
import PagoKycQuestions from './PagoKycQuestions'
import {
  NotEligible,
  PendingReview,
  CircularLoader,
  RegistrationSuccess,
} from './StatusComponents'
import {FooterActions} from './FooterActions'
import PagoIframeTerms from './PagoIframeTerms'

const PhoneNumberStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${media.md`
    gap: 40px;
    padding: 24px;
  `}
`

const KycContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const ResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 100%;
  max-width: 400px;
  min-height: 400px;
  margin: 0 auto;
`

export enum RegistrationStep {
  PhoneNumber = 'phoneNumber',
  Terms = 'terms',
  KYC = 'kyc',
  CreditCheck = 'creditCheck',
}

interface RegistrationStepProps {
  step: RegistrationStep
  setStep: React.Dispatch<React.SetStateAction<RegistrationStep>>
}

export default function PagoRegistrationSteps({
  step,
  setStep,
}: RegistrationStepProps) {
  const {
    loading,
    kycQuestions,
    pagoUserStatus,
    pagoCreditLimit,
    setTermsUrl,
    handleRegistrationUrl,
  } = usePago()
  const {user} = useAuth()

  const initialPhoneNumber = parsePhoneNumberNoPrefix(user?.phoneNumber) || ''
  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber)

  const [kycAnswers, setKycAnswers] = useState<KycAnswer>({})

  const initialAnswers = useMemo(() => {
    const answers: Record<number, string | string[]> = {}
    kycQuestions?.forEach((q) => {
      answers[q.id] = q.type === 'multiple' ? [] : ''
    })
    return answers
  }, [kycQuestions])

  useEffect(() => {
    if (kycQuestions) {
      setKycAnswers(initialAnswers)
    }
  }, [kycQuestions, initialAnswers])

  const handleOnChangePhoneNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = e.target
      // only allow numbers and spaces
      const cleanedValue = value.replace(/[^0-9 ]/g, '')

      setPhoneNumber(cleanedValue)
    },
    [setPhoneNumber],
  )

  const submitType = useCallback(() => {
    switch (pagoUserStatus) {
      case 'NEEDS_TO_PERFORM_KYC':
        return 'kyc'
      case 'NO_REGISTRATION':
        return 'full'
      default:
        return ''
    }
  }, [pagoUserStatus])

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      const answers = kycQuestions?.flatMap((q) => {
        const ansValue = kycAnswers[q.id]
        // If answers[q.id] is an array => create multiple entries
        if (Array.isArray(ansValue)) {
          return ansValue.map((singleAnswer) => ({
            questionId: q.id,
            answer: singleAnswer,
          }))
        }
        // Else it’s a single string => just one entry
        return [{questionId: q.id, answer: ansValue}]
      })

      const response = await handleRegistrationUrl(
        'POST',
        {answers},
        `type=${submitType()}`,
      )

      // TODO: Can we remove KYC_REJECTED here?
      if (
        !!response?.error &&
        (response.error === 'KYC_REJECTED' || response.error === 'KYC_PENDING')
      ) {
        return
      } else {
        setStep(RegistrationStep.Terms)
      }
    },
    [kycQuestions, handleRegistrationUrl, submitType, setStep, kycAnswers],
  )

  const handleClose = useCallback(() => {
    setStep(null)
    setPhoneNumber(initialPhoneNumber)
    setKycAnswers(initialAnswers || {})
    setTermsUrl('')
  }, [initialAnswers, initialPhoneNumber, setStep, setTermsUrl])

  const disabledPhoneNumberButton =
    !isValidPhoneNumber(phoneNumber, 'IS') || !phoneNumber

  const modalTitle = () => {
    switch (step) {
      case RegistrationStep.PhoneNumber:
        return 'Símanúmer'
      case RegistrationStep.Terms:
        return 'Skilmálar'
      default:
        return 'Áreiðanleikakönnun'
    }
  }

  return (
    <Modal
      title={modalTitle()}
      onRequestClose={handleClose}
      isOpen={step !== null}
      bottomMobile
      footerActions={
        <FooterActions
          step={step}
          setStep={setStep}
          onSubmit={handleSubmit}
          disabledPhoneNumberButton={disabledPhoneNumberButton}
          handleClose={handleClose}
          kycAnswers={kycAnswers}
          phoneNumber={phoneNumber}
        />
      }
    >
      {step === RegistrationStep.PhoneNumber && (
        <PhoneNumberStepContainer>
          <P>
            Svo við getum sent skjal til þín í undirritun þurfum við að fá hjá
            þér símanúmer
          </P>
          <NewInput
            id="phoneNumber"
            label="Símanúmer"
            type="tel"
            inputMode="numeric"
            maxLength={7}
            value={phoneNumber}
            onChange={handleOnChangePhoneNumber}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.currentTarget.blur()
              }
            }}
          />
        </PhoneNumberStepContainer>
      )}
      {step === RegistrationStep.KYC && (
        <>
          {loading ? (
            <CircularLoader text="Athugar áreiðanleika. Þetta getur tekið smá stund ..." />
          ) : pagoUserStatus !== 'KYC_REJECTED' &&
            pagoUserStatus !== 'KYC_PENDING' ? (
            <KycContainer>
              <PagoKycQuestions
                kycAnswers={kycAnswers}
                setKycAnswers={setKycAnswers}
              />
            </KycContainer>
          ) : (
            <ResultWrapper>
              {/* TODO: Can we remove KYC_REJECTED here? */}
              {pagoUserStatus === 'KYC_REJECTED' && <NotEligible />}
              {pagoUserStatus === 'KYC_PENDING' && <PendingReview />}
            </ResultWrapper>
          )}
        </>
      )}
      {step === RegistrationStep.Terms && (
        <PagoIframeTerms
          onSigningComplete={() => {
            setTimeout(() => {
              handleRegistrationUrl('GET')

              setStep(RegistrationStep.CreditCheck)
            }, 5000)
          }}
        />
      )}
      {step === RegistrationStep.CreditCheck && (
        <>
          {loading ? (
            <CircularLoader text="Athugar lánshæfi. Þetta getur tekið smá stund ..." />
          ) : (
            <ResultWrapper>
              {pagoUserStatus === 'CREDIT_CHECK_NOT_VALID' && <NotEligible />}
              {pagoUserStatus === 'ELIGABLE' && (
                <RegistrationSuccess amount={pagoCreditLimit} />
              )}
            </ResultWrapper>
          )}
        </>
      )}
    </Modal>
  )
}
